import React from 'react'
import { connect, useDispatch } from 'react-redux'

import { setProjectIdentity } from '@store/actionSlices/projectIdentity'
import type { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import {
  selectFromResult,
  useGetProjectListQuery,
  useLazyGetProjectListQuery,
} from '@api/project-list'

import { SessionMap } from '@firebaseUtil/types'

import pkg from '../../../package.json'
import CreateSessionHandler from './create-session-handler'
import ProjectSwitchHandler from './project-switch-handler'
import RefreshSessionHandler from './refresh-session-handler'
import SessionCard from './session-card'

export interface SessionSettingsProps {
  className?: string
  projectIdentity: ProjectIdentity
  session: SessionMap | undefined
}

function SessionSettings({
  className = '',
  projectIdentity,
  session,
}: SessionSettingsProps) {
  const dispatch = useDispatch()

  const [page, setPage] = React.useState(1)
  const [query, setQuery] = React.useState('')

  const [trigger] = useLazyGetProjectListQuery()

  const { list, pagination, isLoaded } = useGetProjectListQuery(
    {
      page,
      query,
    },
    { selectFromResult }
  )

  const getProjectPage = (status: 'prev' | 'next') => {
    setPage((state: number) => {
      if (status === 'prev') {
        return state > 1 ? state - 1 : 1
      }
      if (status === 'next' && state < pagination.lastPage) {
        return state + 1
      }
      return state
    })
  }

  const getSessionListProjectInfo = React.useCallback(async () => {
    const { sessionList } = projectIdentity

    const promises = Promise.all(
      sessionList.map(async (sess) => {
        if (!sess.projectUUID || !sess.projectName) {
          const response = await trigger({
            page: 1,
            query: sess.projectId.replace(/[^a-zA-Z ]/g, ' '),
          }).unwrap()

          const project = response?.data?.data?.[0]

          if (project) {
            return {
              ...sess,
              projectUUID: project.id,
              projectName: project.name,
            }
          }
          return sess
        }
        return sess
      })
    )

    const validatedSessionList = await promises

    dispatch(
      setProjectIdentity({
        ...projectIdentity,
        sessionList: validatedSessionList,
      })
    )
  }, [])

  React.useEffect(() => {
    const { sessionList, masterKey } = projectIdentity

    if (masterKey) {
      return
    }

    if (sessionList.find((res) => !res.projectUUID || !res.projectName)) {
      getSessionListProjectInfo()
    }
  }, [projectIdentity])

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="p-5">
        <div className="mb-5">
          <RefreshSessionHandler
            projectIdentity={projectIdentity}
            session={session}
          />
        </div>
        <div className="flex items-center">
          <span className="text-title font-medium leading-none text-neutralColour">
            Session settings
          </span>
          <ProjectSwitchHandler
            projectIdentity={projectIdentity}
            setPage={getProjectPage}
            projectList={list}
            pagination={pagination}
            isActive={isLoaded}
            setProjectQuery={(qry) => setQuery(qry)}
          />
        </div>
        <div className="mt-4">
          <CreateSessionHandler
            projectIdentity={projectIdentity}
            setPage={getProjectPage}
            projectList={list}
            pagination={pagination}
            isActive={isLoaded}
            setProjectQuery={(qry) => setQuery(qry)}
          />
        </div>
      </div>
      <div className="auto-row-fr custom-scrollbar grid flex-1 grid-flow-row grid-cols-2 gap-5 overflow-auto px-5 pb-2">
        {projectIdentity.sessionList
          .filter((res) => res.projectId === projectIdentity.projectId)
          .map((sess, index) => (
            <SessionCard
              key={`${sess.sessionId}-${index.toString()}`}
              isActive={sess.sessionId === projectIdentity.sessionId}
              projectIdentity={projectIdentity}
              {...sess}
            />
          ))}
      </div>
      <div className="absolute bottom-5 right-5">
        <p className="text-sm text-neutralColour">
          Remote version: {pkg?.version || '-'}
        </p>
      </div>
    </div>
  )
}

export default connect(
  ({ projectIdentity, firestore: { session } }: RootStateTypeExtra) => ({
    projectIdentity,
    session,
  })
)(SessionSettings)
